<script setup lang="ts">
import { useI18n } from 'petite-vue-i18n';
import { Session } from '../../types/session';
import Button from '../General/Button.vue';
import Heading from '../General/Heading.vue';
import { TEXT } from '../../configs/text';
import { computed, ref } from 'vue';
import { useSessionStore, useSignalRStore, useClassStore } from '../../stores';
import { ClassStudentInfo, ClassSection } from '../../types/class';
import SetPoint from './SetPoint.vue';
import { utils } from '../../utils';


interface Props {
    student: ClassStudentInfo,
    sections: ClassSection[]
}
const { student, sections } = defineProps<Props>()
const emit = defineEmits<{ update: [] }>()

const sessionStore = useSessionStore()
const classStore = useClassStore()
const signalr = useSignalRStore()
let practiceCount = 0;

const pointSessionSelected = ref<Session>(sessionStore.currentSession)

const currentStudent = computed(() =>
    classStore.selectedClassDetails?.students?.find(s => s.id === student.id) || student
)

const bonusPoint = computed<number>(() => getPointForSession(pointSessionSelected.value))

function handleSessionClick(session: Session) {
    pointSessionSelected.value = session
}

function getPointForSession(sessionName: Session): number {
    const points = currentStudent.value.bonus_point
    if (!points || !points.length) return 0;
    return points
        .filter(bp => bp.section_code === sessionName)
        .reduce((sum, bp) => sum + (bp.point || 0), 0)
}

function handlePointUpdate(updatePoint: number) {
    const pointToAdd = updatePoint - bonusPoint.value
    const sectionItem = sections.find(sec => sec.section_code === pointSessionSelected.value)
    signalr.rewardPointStudent(student.id, pointToAdd, pointSessionSelected.value, sectionItem?.course_item_id || null)
    emit('update')
}

const sessionChunks = computed<Session[][]>(() => {
    const total = sessionStore.rewardPointSessions.length;
    if (total <= 5) {
        return [sessionStore.rewardPointSessions];
    } else {
        return [sessionStore.rewardPointSessions.slice(0, Math.ceil(total / 2)), sessionStore.rewardPointSessions.slice(Math.ceil(total / 2))];
    }
});

function formatSessionName(session: Session) : string {
    if (practiceCount === 0) {
        for (const item of sessionStore.rewardPointSessions) {
            if (item === Session.practice || item === Session.practice2) {
                practiceCount++;
            }
        }
    }
    const name = t(utils.getSessionName(session));
    if (session === Session.practice && practiceCount > 1) return `${name} 1`
    if (session === Session.practice2 && practiceCount > 1) return `${name} ${practiceCount}`
    return name
}

const { t } = useI18n()
</script>

<template>
    <Heading alignment="center">{{ t(TEXT.reward_point) }}</Heading>
    <div class="flex flex-col gap-2 items-center justify-center my-5">
        <div v-for="(row, rowIdx) in sessionChunks" :key="'row-' + rowIdx" class="flex flex-row gap-3 justify-center">
            <Button v-for="session in row" :color="pointSessionSelected === session ? 'info' : 'light'" :shadow="false"
                :border="false" class="font-normal text-sm min-w-28 session-button" @click="handleSessionClick(session)" :key="session">
                <div>
                    <div>{{ formatSessionName(session) }}</div>
                    <div class="text-2xl font-bold">{{ getPointForSession(session) }}</div>
                </div>
            </Button>
        </div>
    </div>
    <div class="text-center mb-1">
        {{ t(TEXT.reward_point_for_session, { session: formatSessionName(pointSessionSelected) }) }}
    </div>
    <div class="text-center font-bold text-xl">
        {{ student.full_name }}
    </div>
    <SetPoint :key="bonusPoint" :point="bonusPoint" @update="handlePointUpdate" />
</template>

<style scoped>
.session-button:hover {
    filter: brightness(1.05);
}

.session-button:focus {
    filter: brightness(1.05);
}
</style>